import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  CheckCircle as CheckCircleIcon,
  Science as ScienceIcon,
  MilitaryTech as MilitaryTechIcon,
  Engineering as EngineeringIcon,
  Biotech as BiotechIcon,
  Rocket as RocketIcon
} from '@mui/icons-material';
import { Slide } from '@mui/material';

// Компонент карточки персонажа
const CharacterCard = ({ 
  character, 
  isSelected, 
  onSelect, 
  onEdit, 
  onDelete,
  onActivate,
  onDeactivate,
  showActivation = false,
  races = [],
  index 
}) => {
  // Защита от null/undefined
  if (!character || typeof character !== 'object' || !character.id) {
    console.warn('CharacterCard: invalid character data', character);
    return null;
  }
  
  // Находим расу с защитой от ошибок
  const race = Array.isArray(races) && character.race_id 
    ? races.find(r => r && r.id === character.race_id) 
    : null;
  
  // Определяем иконку в зависимости от типа персонажа с безопасными проверками
  const getTypeIcon = () => {
    if (character.is_npc) {
      return <EngineeringIcon />;
    }
    
    // Безопасная проверка race_id
    const raceId = character.race_id;
    if (!raceId || typeof raceId !== 'string') {
      return <PersonIcon />;
    }
    
    // Используем безопасный метод проверки
    const raceIdLower = raceId.toLowerCase();
    if (raceIdLower.includes('military')) {
      return <MilitaryTechIcon />;
    }
    if (raceIdLower.includes('scientist') || raceIdLower.includes('research')) {
      return <ScienceIcon />;
    }
    if (raceIdLower.includes('pilot') || raceIdLower.includes('flight')) {
      return <RocketIcon />;
    }
    if (raceIdLower.includes('biotech') || raceIdLower.includes('bio')) {
      return <BiotechIcon />;
    }
    return <PersonIcon />;
  };

  const handleClick = () => {
    if (onSelect && character && character.id) {
      onSelect(character.id);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit && character && character.id) {
      onEdit(character.id);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete && character && character.id) {
      onDelete(character.id);
    }
  };

  const handleActivate = (e) => {
    e.stopPropagation();
    if (onActivate && character && character.id) {
      onActivate(character.id);
    }
  };

  const handleDeactivate = (e) => {
    e.stopPropagation();
    if (onDeactivate && character && character.id) {
      onDeactivate(character.id);
    }
  };

  return (
    <Slide 
      direction="up" 
      in={true} 
      timeout={300 + (index * 50)}
      mountOnEnter
      unmountOnExit
    >
      <Card 
        sx={{ 
          mb: 2, 
          cursor: 'pointer',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          transition: 'all 0.3s',
          '&:hover': {
            boxShadow: 6,
            borderColor: 'primary.light',
            transform: 'translateY(-4px)'
          },
          position: 'relative',
          overflow: 'visible'
        }}
        onClick={handleClick}
      >
        {character.is_npc && (
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              borderRadius: '50%',
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              zIndex: 1
            }}
          >
            NPC
          </Box>
        )}
        
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Box sx={{ mr: 1, color: 'primary.main' }}>
                  {getTypeIcon()}
                </Box>
                <Typography 
                  variant="h6" 
                  fontWeight="bold" 
                  noWrap
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1
                  }}
                >
                  {character.name || 'Без имени'}
                </Typography>
                {character.is_active === false && (
                  <Chip 
                    label="Неактивен" 
                    size="small" 
                    color="default" 
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {race?.name || 'Фракция не указана'} • Ранг {character.level || 1}
              </Typography>
              
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {character.is_npc ? (
                  <Chip 
                    label="NPC" 
                    size="small" 
                    color="warning" 
                    variant="filled"
                    icon={<EngineeringIcon />}
                  />
                ) : (
                  <Chip 
                    label="Игрок" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    icon={<PersonIcon />}
                  />
                )}
                
                {character.is_active === true && (
                  <Chip 
                    label="Активен" 
                    size="small" 
                    color="success" 
                    icon={<CheckCircleIcon />}
                  />
                )}
                
                {character.health && typeof character.health === 'object' && (
                  <Chip 
                    label={`Здоровье: ${character.health.current || 0}/${character.health.max || 0}`} 
                    size="small" 
                    variant="outlined"
                  />
                )}
                
                {character.created_at && (
                  <Typography variant="caption" color="text.secondary">
                    Создан: {new Date(character.created_at).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              
              {character.description && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mt: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {character.description}
                </Typography>
              )}
              
              {character.specialization && (
                <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
                  Специализация: {character.specialization}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column', ml: 1 }}>
              {showActivation && (
                <>
                  {!character.is_active && (
                    <Tooltip title="Активировать персонажа">
                      <IconButton
                        size="small"
                        onClick={handleActivate}
                        color="success"
                        sx={{ 
                          bgcolor: 'success.light',
                          '&:hover': { bgcolor: 'success.main', color: 'white' }
                        }}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {character.is_active && (
                    <Tooltip title="Деактивировать персонажа">
                      <IconButton
                        size="small"
                        onClick={handleDeactivate}
                        color="warning"
                        sx={{ 
                          bgcolor: 'warning.light',
                          '&:hover': { bgcolor: 'warning.main', color: 'white' }
                        }}
                      >
                        <PauseIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
              
              <Tooltip title="Редактировать персонажа">
                <IconButton
                  size="small"
                  onClick={handleEdit}
                  color="default"
                  sx={{ 
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Удалить персонажа">
                <IconButton
                  size="small"
                  onClick={handleDelete}
                  color="error"
                  sx={{ 
                    bgcolor: 'error.light',
                    '&:hover': { bgcolor: 'error.main', color: 'white' }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
        
        {/* Прогресс ранга */}
        {character.level_progress !== undefined && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="text.secondary">
                Прогресс ранга
              </Typography>
              <Typography variant="caption" fontWeight="bold">
                {character.level_progress || 0}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={character.level_progress || 0}
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Box>
        )}
      </Card>
    </Slide>
  );
};

CharacterCard.defaultProps = {
  isSelected: false,
  races: [],
  index: 0,
  showActivation: false
};

export default CharacterCard;